import { t } from 'i18next';
import removeMarkdown from 'remove-markdown';

import type { DurationAbbreviation } from '../../types/i18n';

export const formatDurationTag = (seconds: number, { minutesAbbreviation }: Pick<DurationAbbreviation, 'minutesAbbreviation'>) => {
  if (!seconds) return '';

  const minutes = Math.ceil(seconds / 60);

  return `${minutes} ${minutesAbbreviation}`;
};

/**
 * @param duration Duration in seconds
 *
 * Calculates hours and minutes into a string
 * Hours are only shown if at least 1
 * Minutes get rounded
 *
 * @returns string, such as '2hrs 24min' or '31min'
 */

export const formatDuration = (duration: number, { hoursAbbreviation, minutesAbbreviation }: DurationAbbreviation) => {
  if (!duration) return '';

  const hours = Math.floor(duration / 3600);
  const minutes = Math.round((duration - hours * 3600) / 60);

  const hoursString = hours ? `${hours}${hoursAbbreviation}` : '';
  const minutesString = minutes ? `${minutes}${minutesAbbreviation}` : '';

  return [hoursString, minutesString].filter(Boolean).join(' ');
};

export const formatPrice = (price: number, currency: string, country?: string) => {
  return new Intl.NumberFormat(country || 'en-US', {
    style: 'currency',
    currency: currency,
  }).format(price);
};

export const formatSeriesMetaString = (seasonNumber?: string, episodeNumber?: string) => {
  if (!seasonNumber && !episodeNumber) {
    return '';
  }

  const seasonStr = seasonNumber && seasonNumber !== '0' ? t('video:season_number_short', { season: seasonNumber }) : '';
  const episodeStr = episodeNumber ? t('video:episode_number_short', { episode: episodeNumber }) : '';

  return seasonStr ? `${seasonStr}:${episodeStr}` : episodeStr;
};

export const formatVideoScheduleTime = (locale: string, scheduledStart: Date | undefined, scheduledEnd: Date | undefined) => {
  if (!scheduledStart) {
    return '';
  }

  if (!scheduledEnd) {
    return formatLocalizedTime(scheduledStart, locale);
  }

  return `${formatLocalizedTime(scheduledStart, locale)} - ${formatLocalizedTime(scheduledEnd, locale)}`;
};

export const formatLocalizedDate = (date: Date, locale: string) => {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
};

export const formatLocalizedDateShort = (date: Date, locale: string) => {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', weekday: 'short' }).format(date);
};

export const formatLocalizedTime = (date: Date, locale: string) => {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: 'numeric' }).format(date);
};

export const formatLocalizedDateTime = (date: Date, locale: string, separator = ' ') => {
  return `${formatLocalizedDate(date, locale)}${separator}${formatLocalizedTime(date, locale)}`;
};

// Should only be used for numerical and range based ratings (eg. 1, 2-5)
export const formatAgeRating = (text: string | undefined) => {
  if (!text) return '';
  const ageRanges = text.split('-').map((age) => age.trim());
  return `${ageRanges[0]}+`;
};

export function stripMarkdown(text: string): string {
  if (!text) return '';

  return removeMarkdown(text, {
    gfm: true,
    useImgAltText: false,
  });
}
